import { Button } from '../Button/Button';
import { ProgressBar, type ProgressBarType } from '../ProgressBar/ProgressBar';
import { SparkIcon } from '../SparkIcon';
import { TagColored, type TagColoredVariant } from '../Tag/TagColored';
import { TruncatedText } from '../Text/TruncatedText';
import './Widgets.css';

/**
 * Card tag whose label shrinks to the room left by the value / Review button
 * and ends in "…" instead of pushing them out of the card. The full text is
 * shown in a tooltip only while it is actually clipped.
 */
function CardTag({ label, variant }: { label: string; variant: TagColoredVariant }) {
  return (
    <TagColored
      label={<TruncatedText text={label} maxWidth="100%" />}
      variant={variant}
      className="spark-validation-card-tag"
    />
  );
}

export type ValidationCardType = 'text-only' | 'tag';
export type ValidationCardBorder = boolean;

/** Dashboard widget card showing validation metrics with optional tag, review button, and progress bar. */
export type ValidationCardProps = {
  /** `'text-only'` shows plain value rows; `'tag'` includes a colored tag and optional review action. */
  type?: ValidationCardType;
  border?: ValidationCardBorder;
  topTitle?: string;
  bottomTitle?: string;
  topValue?: string;
  bottomValue?: string;
  tagLabel?: string;
  /** Occurrence count. Shown inside the tag when `showReviewButton` is set (`label (count)`); otherwise as the large value next to the tag. */
  tagTopValue?: string;
  tagVariant?: TagColoredVariant;
  /** Shows a View/Review action button in the header row (tag variant only). */
  showReviewButton?: boolean;
  reviewLabel?: string;
  onReviewClick?: () => void;
  reviewDisabled?: boolean;
  /** When true, View shows a trailing filter icon (ranked-queue table filter is active). */
  reviewActive?: boolean;
  /** Shows a ProgressBar below the value rows. */
  showProgress?: boolean;
  progressValue?: number;
  /** ProgressBar color. Use `'success'` when the review goal is met. */
  progressType?: ProgressBarType;
  /** Optional muted helper line below the progress bar. */
  footerNote?: string;
  className?: string;
};

export function ValidationCard({
  type = 'text-only',
  border = false,
  topTitle = 'Overview / Total extractions',
  bottomTitle = 'Entities Validated',
  topValue = '189542',
  bottomValue = '1',
  tagLabel = 'TEST',
  tagTopValue = '2143',
  tagVariant = 'blue',
  showReviewButton = false,
  reviewLabel = 'Review',
  onReviewClick,
  reviewDisabled = false,
  reviewActive = false,
  showProgress = false,
  progressValue = 0,
  progressType = 'default',
  footerNote,
  className = '',
}: ValidationCardProps) {
  const rootClasses = [
    'spark-validation-card',
    border ? 'spark-validation-card-border' : '',
    type === 'text-only' ? 'spark-validation-card-type-text-only' : 'spark-validation-card-type-tag',
    showProgress ? 'spark-validation-card-has-footer' : '',
    footerNote ? 'spark-validation-card-has-footer-note' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses}>
      <div className="spark-widget-content">
        {type === 'tag' && showReviewButton ? (
          <>
            {/* Header: tag with occurrence count · Review button */}
            <div className="spark-validation-card-header-with-review">
              <CardTag
                label={tagTopValue ? `${tagLabel} (${tagTopValue})` : tagLabel}
                variant={tagVariant}
              />
              <Button
                variant="secondary"
                size="tiny"
                type="button"
                disabled={reviewDisabled}
                onClick={onReviewClick}
                endIcon={reviewActive ? <SparkIcon slug="filter" size="small" /> : undefined}
                aria-pressed={reviewActive}
              >
                {reviewLabel}
              </Button>
            </div>

            {/* Footer row */}
            <div className="spark-widget-row">
              <p className="spark-widget-label spark-widget-footer-label">{bottomTitle}</p>
              <p className="spark-widget-value-md">{bottomValue}</p>
            </div>
          </>
        ) : (
          <>
            {/* Top row: label/tag + value side-by-side */}
            <div className="spark-widget-row">
              {type === 'text-only' ? (
                <p className="spark-widget-label">{topTitle}</p>
              ) : (
                <CardTag label={tagLabel} variant={tagVariant} />
              )}
              <p className="spark-widget-value-lg">{type === 'text-only' ? topValue : tagTopValue}</p>
            </div>

            {/* Bottom row: footer label + value */}
            <div className="spark-widget-row">
              <p className="spark-widget-label spark-widget-footer-label">{bottomTitle}</p>
              <p className="spark-widget-value-md">{bottomValue}</p>
            </div>
          </>
        )}

        {/* Optional progress bar */}
        {showProgress && (
          <ProgressBar
            value={progressValue}
            type={progressType}
            showPercentage
            className="spark-validation-card-progress"
          />
        )}

        {/* Optional helper note */}
        {footerNote && (
          <p className="spark-validation-card-footer-note">{footerNote}</p>
        )}
      </div>
    </div>
  );
}
